import { AfterViewChecked, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './modal.component.html'
})
export class ModalComponent implements OnChanges, AfterViewChecked {
  @Input() open = false;
  @Input() title = '';
  @Input() maxWidth: 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() closeOnBackdrop = true;
  @Output() close = new EventEmitter<void>();

  @ViewChild('dialog') dialog?: ElementRef<HTMLElement>;

  private dialogWasOpen = false;
  private previouslyFocused: HTMLElement | null = null;

  get maxWidthClass(): string {
    switch (this.maxWidth) {
      case 'sm':
        return 'max-w-sm';
      case 'lg':
        return 'max-w-lg';
      case 'xl':
        return 'max-w-xl';
      default:
        return 'max-w-md';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue && !changes['open']?.previousValue) {
      this.previouslyFocused = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    }
  }

  ngAfterViewChecked(): void {
    if (this.open && !this.dialogWasOpen) {
      this.dialogWasOpen = true;
      setTimeout(() => this.focusFirstElement());
    } else if (!this.open && this.dialogWasOpen) {
      this.dialogWasOpen = false;
      this.previouslyFocused?.focus();
      this.previouslyFocused = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open && this.closeOnBackdrop) {
      this.close.emit();
    }
  }

  @HostListener('document:keydown.tab', ['$event'])
  onTab(event: KeyboardEvent): void {
    if (!this.open || !this.dialog) {
      return;
    }
    const focusable = Array.from(this.dialog.nativeElement.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    ));
    if (!focusable.length) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onBackdropClick(): void {
    if (this.closeOnBackdrop) {
      this.close.emit();
    }
  }

  private focusFirstElement(): void {
    const element = this.dialog?.nativeElement;
    if (!element) {
      return;
    }
    const first = element.querySelector<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    (first || element).focus();
  }
}
